import type { UUID } from '@cmg/common';
import { Button, SnackbarManager, Stack, useDialogState } from '@cmg/design-system';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import type { InvestorEmployeeCreateDtoInput } from '../../../../../../../../../graphql';
import { refetchFirmInvestorContactsGridReactiveVar } from '../../FirmsInvestorContactsGrid.model';
import { DialogMode, InvestorContactDialog } from '../InvestorContactDialog/InvestorContactDialog';
import { INITIAL_VALUES, newInvestorContactValidationSchema } from './AddInvestorContact.model';
import { useAddInvestorFirmsContact } from './hooks/useAddInvestorFirmsContact';

export function AddInvestorContact() {
  const { crmIntegrationId, firmId = '' } = useParams<{
    crmIntegrationId?: UUID;
    firmId: string;
  }>();

  const { addInvestorFirmContact, error } = useAddInvestorFirmsContact(crmIntegrationId);

  const { isOpen, onOpen, onClose } = useDialogState();

  const handleOnSubmit = useCallback(
    async (contactData: InvestorEmployeeCreateDtoInput) => {
      try {
        await addInvestorFirmContact({
          input: {
            firmKeyOrId: firmId,
            input: {
              ...contactData,
            },
          },
        });
        refetchFirmInvestorContactsGridReactiveVar(true);
        onClose();
        SnackbarManager.success('Investor contact added successfully.');
      } catch (error) {
        SnackbarManager.error('Failed to add investor contact.');
        throw error;
      }
    },
    [addInvestorFirmContact, onClose, firmId]
  );

  return (
    <Stack>
      <Button onClick={onOpen} variant="outlined">
        Add Investor Contact
      </Button>
      <InvestorContactDialog<InvestorEmployeeCreateDtoInput>
        mode={DialogMode.Create}
        open={isOpen}
        error={error}
        initialValues={INITIAL_VALUES}
        validationSchema={newInvestorContactValidationSchema}
        onClose={onClose}
        onSubmit={handleOnSubmit}
      />
    </Stack>
  );
}
